import React from "react";
import { useField } from 'formik';
import l from "components/util/l"
import BaseField from "components/util/schema_form/fields/BaseField";


const NumberField = ({
	name,
	label,
	value,
	values,
	required,
	disabled,
	caption,
	helpMessage,
	allowDecimals, // Decimal or Integer
	minValue, // Lower range for the number field
	maxValue, // Upper range for the number field
	step, // Step for the number field
	placeholder,
	enableComments,
	setFieldValue
}) => {

	const [field, meta, helpers] = useField(name);
	const currentValue = field.value;
	const regex = allowDecimals ? /^-?\d*(\.\d*)?$/ : /^-?\d*$/;

	const hasMax = maxValue !== undefined && maxValue !== null;
	const hasMin = minValue !== undefined && minValue !== null;

	const getDecimalPlaces = (num) => {
		if (!num || Number.isInteger(num)) return 0;
		const parts = num.toString().split('.');
		return parts.length > 1 ? parts[1].length : 0;
	}

	const roundToStep = (value, step) => {
		const decimals = getDecimalPlaces(step);
		return parseFloat(value.toFixed(decimals));
	}

	const increment = () => {
		const effectiveStep = parseFloat(step) || 1;
		let newVal = parseFloat(currentValue || 0) + effectiveStep;
		if (hasMax && newVal > maxValue) newVal = maxValue;
		if (!allowDecimals) newVal = Math.round(newVal);
		else newVal = roundToStep(newVal, effectiveStep);
		setFieldValue(name, newVal);
	};

	const decrement = () => {
		const effectiveStep = parseFloat(step) || 1;
		let newVal = parseFloat(currentValue || 0) - effectiveStep;
		if (hasMin && newVal < minValue) newVal = minValue;
		if (!allowDecimals) newVal = Math.round(newVal);
		else newVal = roundToStep(newVal, effectiveStep);
		setFieldValue(name, newVal);
	}

	// Intercepts keys before the browser processes them:
	// - Arrow up/down: use our step logic instead of the browser's native +/-1
	// - Blocks characters that would produce an invalid value (e, +, out-of-range -, etc.)
	const handleKeyDown = (e) => {

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			increment();
			return;
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			decrement();
			return;
		}

		const navigationKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];
		if (navigationKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;

		if (/^\d$/.test(e.key)) return;

		if (e.key === '-') {
			const canBeNegative = !hasMin || minValue < 0;
			if (canBeNegative && e.target.selectionStart === 0 && !e.target.value.includes('-')) return;
			e.preventDefault();
			return;
		}

		if (e.key === '.') {
			if (allowDecimals && !e.target.value.includes('.')) return;
			e.preventDefault();
			return;
		}

		e.preventDefault();

	}

	const handleChange = (e) => {

		const inputVal = e.target.value;
		if (inputVal === '') {
			setFieldValue(name, inputVal);
			return;
		}

		// Allow '-' as an intermediate state only when negatives are valid
		if (inputVal === '-') {
			const canBeNegative = !hasMin || minValue < 0;
			if (canBeNegative) setFieldValue(name, inputVal);
			return;
		}

		if (!regex.test(inputVal)) 
			return;

		// Enforce range during typing: clamp to [minValue, maxValue] instead of blocking
		const num = parseFloat(inputVal);
		if (!isNaN(num)) {
			if (hasMax && num > maxValue) {
				setFieldValue(name, maxValue);
				return;
			}
			if (hasMin && num < minValue) {
				setFieldValue(name, minValue);
				return;
			}
		}

		setFieldValue(name, inputVal);

	}

	const handleBlur = (e) => {

		field.onBlur(e);

		const val = e.target.value;
		if (val === '' || val === '-') {
			if (hasMin) setFieldValue(name, minValue);
			return;
		}

		const num = parseFloat(val);
		if (isNaN(num)) {
			if (hasMin) setFieldValue(name, minValue);
			return;
		}

		let clamped = num;
		if (hasMin && clamped < minValue) clamped = minValue;
		if (hasMax && clamped > maxValue) clamped = maxValue;
		if (clamped !== num) setFieldValue(name, clamped);
		
	}

	return (
		<BaseField
			name={name}
			label={label}
			values={values}
			required={required}
			caption={caption}
			helpMessage={helpMessage}
			enableComments={enableComments}
			setFieldValue={setFieldValue}
		>
			<div className="input-group-quantity-counter w-100">
				<input
					id={name}
					name={name}
					type="text"
					inputMode={allowDecimals ? "decimal" : "numeric"}
					value={currentValue ?? ''}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					required={required}
					disabled={disabled}
					autoComplete="off"
					placeholder={l(placeholder)}
					className="form-control input-group-quantity-counter-control"
					style={{ paddingLeft: "1.2rem", paddingRight: "1.2rem" }}
				/>

				<div className="input-group-quantity-counter-toggle">
					<button type="button" className="input-group-quantity-counter-btn mr-1" onClick={decrement} disabled={disabled}>
						<i className="tio-remove"></i>
					</button>
					<button type="button" className="input-group-quantity-counter-btn" onClick={increment} disabled={disabled}>
						<i className="tio-add"></i>
					</button>
				</div>
			</div>
		</BaseField>
	)
}

export default NumberField;